import React, { useState, useEffect, useMemo } from "react";
import { addDays, isValid, parseISO } from "date-fns";
import { TripX } from "@/types/trip";
import { useNavigate } from "react-router-dom";
import Calendar from "./Calendar";
import useLodgeStore from "@/stores/lodge/lodgeStore";

interface UpNextProps {
  trips: TripX[];
  selectedTrip: TripX | null;
  setSelectedTrip: (trip: TripX | null) => void;
}

const UpNext: React.FC<UpNextProps> = ({
  trips,
  selectedTrip,
  setSelectedTrip,
}) => {
  const navigate = useNavigate();
  const [startBlock, setStartBlock] = useState<Date | null>(null);
  const [endBlock, setEndBlock] = useState<Date | null>(null);

  // Safe date parsing function
  const safeParseDate = (dateString: string): Date | null => {
    try {
      const parsedDate = parseISO(dateString);
      return isValid(parsedDate) ? parsedDate : null;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };

  useEffect(() => {
    if (selectedTrip?.locations && selectedTrip.locations.length > 0) {
      const firstLocation = selectedTrip.locations[0];
      const lastLocation =
        selectedTrip.locations[selectedTrip.locations.length - 1];

      if (firstLocation?.date?.start_date) {
        const startDate = safeParseDate(firstLocation.date.start_date);
        if (startDate) {
          setStartBlock(startDate);
          setEndBlock(addDays(startDate, 3));
        }
      }
    }
  }, [selectedTrip]);

  useEffect(() => {
    if (trips && trips.length > 0 && !selectedTrip) {
      // Explicitly check that trips[0] is defined before setting
      const firstTrip = trips[0];
      if (firstTrip) {
        setSelectedTrip(firstTrip);
      }
    }
  }, [trips, selectedTrip, setSelectedTrip]);

  const handleCreateTrip = () => {
    navigate("/new-trip");
  };
  const handleSelectTrip = (trip: TripX) => {
    setSelectedTrip(trip);
  };

  const handlePrevBlock = () => {
    if (startBlock && endBlock) {
      setStartBlock(addDays(startBlock, -1));
      setEndBlock(addDays(endBlock, -1));
    }
  };

  const handleNextBlock = () => {
    if (startBlock && endBlock) {
      setStartBlock(addDays(startBlock, 1));
      setEndBlock(addDays(endBlock, 1));
    }
  };

  if (!startBlock || !endBlock) {
    return <div>Loading trip dates...</div>;
  }

  return (
    <div className="flex-grow overflow-y-auto h-full">
      <Calendar
        startBlock={startBlock}
        endBlock={endBlock}
        selectedTrip={selectedTrip}
        onPrevBlock={handlePrevBlock}
        onNextBlock={handleNextBlock}
      />
    </div>
  );
};

export default UpNext;
